import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Clock, MessageCircle, Instagram, Mail } from "lucide-react";

export default function Contact() {
  const [whatsapp, setWhatsapp] = useState("");
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/config").then(({ data }) => setWhatsapp(data.whatsapp)).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSending(true);
    // For a small business, route messages via WhatsApp so nothing is lost
    const wa = whatsapp.replace(/[^0-9]/g, "");
    const msg = encodeURIComponent(
      `Hi Ave_luxe_candles!\n\nName: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
    );
    window.open(`https://wa.me/${wa}?text=${msg}`, "_blank");
    setForm({ name: "", email: "", message: "" });
    toast.success("Opening WhatsApp — send the message to reach us instantly.");
    setSending(false);
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-[#E3DCD2] bg-[#F3EFEA]/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-[11px] uppercase tracking-widest-plus text-[#D27D60] mb-4">✦ Contact</div>
          <h1 className="font-serif-display text-6xl md:text-7xl leading-[0.95] max-w-3xl">
            Say <em className="text-[#D27D60]">hello</em>.
            <br />We'd love to hear from you.
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5 space-y-8">
          <div>
            <div className="text-[11px] uppercase tracking-widest-plus text-[#9BA595] mb-3">Visit our studio</div>
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-[#D27D60] mt-1" strokeWidth={1.5} />
              <div>
                <div className="font-serif-display text-2xl">Mourigram, Howrah</div>
                <div className="text-sm text-[#4a4a4a]">West Bengal 711109, India</div>
              </div>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest-plus text-[#9BA595] mb-3">Hours</div>
            <div className="flex items-start gap-3">
              <Clock className="w-4 h-4 text-[#D27D60] mt-1" strokeWidth={1.5} />
              <div>
                <div className="text-sm">Open now · Closes 5pm</div>
                <div className="text-xs text-[#9BA595] mt-1">Mon – Sat · By appointment on Sundays</div>
              </div>
            </div>
          </div>

          {whatsapp && (
            <div>
              <div className="text-[11px] uppercase tracking-widest-plus text-[#9BA595] mb-3">Message us</div>
              <a
                href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-3 border border-[#25D366] text-[#25D366] px-5 py-3 rounded-full text-sm uppercase tracking-widest-plus hover:bg-[#25D366] hover:text-white transition-colors"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                WhatsApp us
              </a>
            </div>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-widest-plus text-[#9BA595] mb-3">Follow along</div>
            <div className="flex gap-3">
              <a href="https://instagram.com/ave_luxe_candles" target="_blank" rel="noreferrer" className="p-3 rounded-full border border-[#E3DCD2] hover:border-[#D27D60] hover:text-[#D27D60] transition-colors" data-testid="contact-instagram">
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a href="mailto:hello@aveluxecandles.in" className="p-3 rounded-full border border-[#E3DCD2] hover:border-[#D27D60] hover:text-[#D27D60] transition-colors" data-testid="contact-email">
                <Mail className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="md:col-span-7 p-8 md:p-10 bg-[#F3EFEA]/60 rounded-lg space-y-5" data-testid="contact-form">
          <div className="text-xs uppercase tracking-widest-plus text-[#9BA595]">Send us a note</div>
          <input
            required
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors"
            data-testid="contact-name-input"
          />
          <input
            required
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors"
            data-testid="contact-email-input"
          />
          <textarea
            required
            rows={6}
            placeholder="What would you like to talk about?"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full bg-transparent border border-[#E3DCD2] rounded-md px-4 py-3 text-sm outline-none focus:border-[#D27D60] transition-colors resize-none"
            data-testid="contact-message-input"
          />
          <button
            type="submit"
            disabled={sending}
            className="bg-[#1A1A1A] text-[#FDFBF7] px-7 py-3.5 rounded-full text-sm uppercase tracking-widest-plus hover:bg-[#D27D60] transition-colors disabled:opacity-50"
            data-testid="contact-submit-btn"
          >
            {sending ? "Sending..." : "Send message"}
          </button>
          <p className="text-[10px] uppercase tracking-widest-plus text-[#9BA595]">
            We reply within 24 hours, usually much sooner via WhatsApp.
          </p>
        </form>
      </section>
    </div>
  );
}
